import { useEffect, useMemo, useState } from 'react';
import { ClipboardList, Link2, Star } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import type { SurveyQuestion } from '../../lib/types/survey';

/**
 * Sezione "Sondaggi" della scheda contatto (lead o cliente): risposte collegate al contatto
 * (link personale o email) con le stelle, dettaglio aperto nella riga, e copia del link
 * personale per inviarlo (edge `survey-patient-link`, la risposta torna su questa scheda).
 */

interface ResponseRow {
  id: string;
  survey_id: string;
  answers: Record<string, unknown>;
  completed_at: string | null;
  created_at: string;
  metadata: { ratings?: { min: number | null; avg: number | null; all_max: boolean | null } } | null;
}

interface SurveyRow {
  id: string;
  title: string;
  slug: string;
  status: string;
  questions: SurveyQuestion[];
}

const HIDDEN_TYPES = new Set(['welcome-screen', 'statement', 'ending']);

function formatAnswer(question: SurveyQuestion, value: unknown): string {
  if (value === undefined || value === null || value === '') return '—';
  if (question.type === 'rating') {
    const max = question.attributes.max ?? 5;
    const n = Number(value);
    return `${'★'.repeat(n)}${'☆'.repeat(Math.max(0, max - n))}  ${n}/${max}`;
  }
  if (Array.isArray(value)) {
    const labels = question.attributes.choices ?? [];
    return value.map((v) => labels.find((c) => c.value === v)?.label ?? String(v)).join(', ');
  }
  if (question.type === 'dropdown' || question.type === 'multiple-choice') {
    return question.attributes.choices?.find((c) => c.value === value)?.label ?? String(value);
  }
  if (typeof value === 'object') return Object.values(value as Record<string, unknown>).filter(Boolean).join(' · ');
  return String(value);
}

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });

export function ClientSurveysSection({ clientId }: { clientId: string }) {
  const [responses, setResponses] = useState<ResponseRow[]>([]);
  const [surveys, setSurveys] = useState<Record<string, SurveyRow>>({});
  const [activeSurveys, setActiveSurveys] = useState<SurveyRow[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [linkSurveyId, setLinkSurveyId] = useState('');
  const [copying, setCopying] = useState(false);

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    (async () => {
      const { data: rows } = await supabase
        .from('survey_responses')
        .select('id, survey_id, answers, completed_at, created_at, metadata')
        .or(`client_id.eq.${clientId},lead_id.eq.${clientId}`)
        .order('created_at', { ascending: false })
        .limit(50);
      const { data: active } = await supabase
        .from('surveys')
        .select('id, title, slug, status, questions')
        .eq('status', 'active')
        .order('created_at', { ascending: false });
      const ids = [...new Set((rows || []).map((r) => r.survey_id))];
      const { data: answered } = ids.length
        ? await supabase.from('surveys').select('id, title, slug, status, questions').in('id', ids)
        : { data: [] as SurveyRow[] };
      if (cancelled) return;
      setResponses((rows || []) as ResponseRow[]);
      setSurveys(Object.fromEntries([...(active || []), ...(answered || [])].map((s) => [s.id, s as SurveyRow])));
      setActiveSurveys((active || []) as SurveyRow[]);
      setLinkSurveyId((active || [])[0]?.id || '');
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  const openResponse = useMemo(() => responses.find((r) => r.id === openId) || null, [responses, openId]);

  const copyLink = async () => {
    if (!linkSurveyId) return;
    setCopying(true);
    try {
      const { data, error } = await supabase.functions.invoke('survey-patient-link', {
        body: { survey_id: linkSurveyId, client_id: clientId },
      });
      if (error || !data?.success) throw new Error(data?.error || error?.message);
      const url = `${window.location.origin}/s/${data.slug}?p=${encodeURIComponent(data.token)}`;
      await navigator.clipboard.writeText(url);
      toast.success('Link del sondaggio copiato: la risposta arriverà su questa scheda');
    } catch {
      toast.error('Impossibile generare il link del sondaggio');
    } finally {
      setCopying(false);
    }
  };

  if (responses.length === 0 && activeSurveys.length === 0) return null;

  return (
    <>
      <div className="border-b border-[rgba(55,53,47,0.09)] dark:border-gray-700 my-4" />
      <div className="flex items-center justify-between py-2 mb-1">
        <div className="flex items-center space-x-2">
          <h3 className="text-xs font-medium text-[#787774] dark:text-gray-400 uppercase tracking-wide">Sondaggi</h3>
          <span className="text-xs text-[#787774] dark:text-gray-500">({responses.length})</span>
        </div>
      </div>

      <div className="space-y-0.5">
        {responses.map((r) => {
          const survey = surveys[r.survey_id];
          const ratings = r.metadata?.ratings;
          const isOpen = openId === r.id;
          return (
            <div key={r.id}>
              <button
                type="button"
                onClick={() => setOpenId(isOpen ? null : r.id)}
                className="w-full text-left py-2 px-2 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.03] rounded transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 min-w-0 flex-1">
                    <ClipboardList className="h-4 w-4 text-[#787774] dark:text-gray-500 flex-shrink-0" />
                    <span className="text-sm text-[#37352F] dark:text-white truncate">{survey?.title || 'Sondaggio'}</span>
                  </div>
                  {ratings?.avg != null && (
                    <span className="flex items-center gap-1 text-xs font-medium text-[#37352F] dark:text-white flex-shrink-0 ml-2">
                      <Star className="h-3.5 w-3.5 text-[#D9730D]" fill="#D9730D" />
                      {ratings.all_max ? 'Tutte 5' : `min ${ratings.min} · media ${ratings.avg}`}
                    </span>
                  )}
                </div>
                <div className="ml-6 mt-0.5 text-xs text-[#787774] dark:text-gray-500">
                  {formatDate(r.completed_at || r.created_at)}
                </div>
              </button>

              {isOpen && openResponse && survey && (
                <div className="ml-8 mr-2 mb-2 space-y-2">
                  {survey.questions.filter((q) => !HIDDEN_TYPES.has(q.type) && openResponse.answers[q.id] !== undefined).map((q) => (
                    <div key={q.id}>
                      <div className="text-xs text-[#787774] dark:text-gray-400">{q.attributes.label}</div>
                      <div className="text-sm text-[#37352F] dark:text-white whitespace-pre-line">{formatAnswer(q, openResponse.answers[q.id])}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {activeSurveys.length > 0 && (
        <div className="flex items-center gap-2 px-2 pt-2">
          <Link2 className="h-3.5 w-3.5 text-[#787774] dark:text-gray-500 flex-shrink-0" />
          {activeSurveys.length > 1 ? (
            <select
              value={linkSurveyId}
              onChange={(e) => setLinkSurveyId(e.target.value)}
              className="flex-1 min-w-0 text-xs text-[#37352F] dark:text-gray-200 bg-transparent border-0 focus:ring-0 p-0 truncate cursor-pointer"
            >
              {activeSurveys.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
            </select>
          ) : (
            <span className="flex-1 min-w-0 text-xs text-[#37352F] dark:text-gray-200 truncate">{activeSurveys[0].title}</span>
          )}
          <button
            type="button"
            onClick={copyLink}
            disabled={copying}
            className="text-xs text-[#337EA9] dark:text-[#529CCA] hover:underline disabled:opacity-50 flex-shrink-0"
          >
            {copying ? 'Genero…' : 'Copia link'}
          </button>
        </div>
      )}
    </>
  );
}
